import { pianoParser } from "../../../../../common/parameters";
import { IBarline, IParsedDo, IPitch, IScore } from "../../../../../common/types";
import piano from "../../../../piano";
import { parseDo } from "../../../Score/methods/changeDo";
import { IAccidental } from "../../../Score/score-display/utils";
import getKeyOffset from "../Common/getKeyOffset";
import { IState, IHintableProps } from "./interfaces";
import { IMelodyTableNote, ISolutionNote, ITone } from "./MelodyTableBar/interfaces";
// single: 12,
// double: 18,
// final: 21,

export const gapWidth = (width: number) => (width / 100) * 8;
export const normalBarlineWidth = (width: number) => (width / 100) * 6; // => 6 / 12
export const boldBarlineWidth = (width: number) => normalBarlineWidth(width) * 2.5; // => 6 / 12
export const doubleBarlineWidth = (width: number) => normalBarlineWidth(width) * 2 + gapWidth(width); // => 20 / 18
export const finalBarlineWidth = (width: number) => normalBarlineWidth(width) + gapWidth(width) + boldBarlineWidth(width); // => 16.5 / 21
export const dotsWidth = (width: number, height: number) => ((height * width) / 1000) * 4;

export const loadPiano = (scale: ITone[], hasSharp: boolean, hasFlat: boolean) => {
    const notesToLoad = new Set<number>();
    scale.forEach(el => {
        notesToLoad.add(el.piano);
        if (el.a === 0) {
            if (hasSharp && scale.findIndex(tone => tone.piano === el.piano + 1 && tone.tone === el.tone) === -1) {
                notesToLoad.add(el.piano + 1);
                scale.push({
                    tone: el.tone,
                    piano: el.piano + 1,
                    a: 1,
                    pitch: el.pitch,
                });
            }
            if (hasFlat && scale.findIndex(tone => tone.piano === el.piano - 1 && tone.tone === el.tone) === -1) {
                notesToLoad.add(el.piano - 1);
                scale.push({
                    tone: el.tone,
                    piano: el.piano - 1,
                    a: -1,
                    pitch: el.pitch,
                });
            }
        } else if (el.a === -1) {
            if (scale.findIndex(tone => tone.piano === el.piano + 1 && tone.tone === el.tone) === -1) {
                notesToLoad.add(el.piano + 1);
                scale.push({
                    tone: el.tone,
                    piano: el.piano + 1,
                    a: 0,
                    pitch: el.pitch,
                });
            }
            if (hasSharp && scale.findIndex(tone => tone.piano === el.piano + 2 && tone.tone === el.tone) === -1) {
                notesToLoad.add(el.piano + 2);
                scale.push({
                    tone: el.tone,
                    piano: el.piano + 2,
                    a: 1,
                    pitch: el.pitch,
                });
            }
        } else if (el.a === 1) {
            if (scale.findIndex(tone => tone.piano === el.piano - 1 && tone.tone === el.tone) === -1) {
                notesToLoad.add(el.piano - 1);
                scale.push({
                    tone: el.tone,
                    piano: el.piano - 1,
                    a: 0,
                    pitch: el.pitch,
                });
            }
            if (hasFlat && scale.findIndex(tone => tone.piano === el.piano - 2 && tone.tone === el.tone) === -1) {
                notesToLoad.add(el.piano - 2);
                scale.push({
                    tone: el.tone,
                    piano: el.piano - 2,
                    a: -1,
                    pitch: el.pitch,
                });
            }
        }
    });
    scale.sort((a, b) => a.tone - b.tone);
    [...notesToLoad].forEach(note => {
        piano.load(note);
    });
};

export const getBarlinesWidth = (start: boolean, barlines: IBarline[], total: number, width: number, height: number, returnType = "single") => {
    let barlineWidth = 0;
    const barlineWidths = [];
    let lastWidth = 0;
    for (let i = start ? 0 : 1; i <= total; i++) {
        let barlineIndex = barlines.findIndex(barline => barline.bar === i);
        if (barlineIndex !== -1 && barlines[barlineIndex].type) {
            const barline = barlines[barlineIndex];
            if (barline.type!.indexOf("double") === 0) {
                barlineWidth += doubleBarlineWidth(width);
            } else {
                barlineWidth += finalBarlineWidth(width);
            }
            if (barline.type!.indexOf("RepeatEnd") !== -1 || barline.type!.indexOf("RepeatStart") !== -1) {
                barlineWidth += dotsWidth(width, height);
            }
            if (barline.type!.indexOf("RepeatBoth") !== -1) {
                barlineWidth += dotsWidth(width, height);
                barlineWidth += dotsWidth(width, height);
                if (barline.type!.indexOf("final") === 0) {
                    barlineWidth += normalBarlineWidth(width);
                    barlineWidth += gapWidth(width);
                }
            }
        } else {
            barlineWidth += normalBarlineWidth(width);
        }
        barlineWidths[i] = barlineWidth - lastWidth;
        lastWidth = barlineWidth;
    }
    if (returnType === "single") {
        return barlineWidth;
    } else if (returnType === "array") {
        return barlineWidths;
    }
};

export const getFullToneset = (scale: ITone[]) => {
    const fullToneset = [];
    for (let a = scale[0].tone; a <= scale[scale.length - 1].tone; a++) {
        fullToneset.push(a);
    }
    return fullToneset;
};

const getPitch = (p: IPitch, doPos: number) => {
    let pos = doPos;
    pos += (p.o || 0) * 7;
    pos += solfaParser[p.s];
    return pos;
};

const solfaParser = [3, 0, 4, 1, 5, 2, 6];

export const parseScore = (score: IScore, accidentals: IAccidental[], isToneset?: boolean) => {
    let bars: IMelodyTableNote[][] = [];
    let scale: ITone[] = [];
    let width = 0;
    let solutions: (ISolutionNote | null)[][] = [];
    let hasSharp = false,
        hasFlat = false;
    const octave = score.octave0 || 0;
    for (let index = 0; index < score.bars.length; index++) {
        const doPos = parseDo(accidentals[index]);
        let bar = score.bars[index]!;
        bars.push([]);
        solutions.push([]);
        if (!bar) continue;
        for (let index = 0; index < bar.notes.length; index++) {
            let note = bar.notes[index];
            let p: number | null = null;
            solutions[solutions.length - 1].push({} as ISolutionNote);
            if (note.p) {
                p = getPitch(note.p as IPitch, doPos.value) + octave * 7;
                if (score.doClef && score.doClefOffset?.[0]) {
                    p += score.doClefOffset[0];
                }
            } else if (!note.r) {
                return false;
            }
            const a = note.p ? (note.p as IPitch).a || 0 : 0;
            if (isToneset) {
                solutions[solutions.length - 1][solutions[solutions.length - 1].length - 1] = {
                    p: p!,
                    a,
                };
            }

            width += (4 / note.l) * (note.d ? 1.5 : 1);
            bars[bars.length - 1].push({
                l: note.l,
                p,
                a,
                d: note.d,
                // s: note.p ? note.p.sf : null, // TODO: remove that?
                t: note.t,
                tie: note.tie,
                pitch: note.p as IPitch,
            });
            if (note.p) {
                if ((note.p as IPitch).a === 1) hasSharp = true;
                if ((note.p as IPitch).a === -1) hasFlat = true;
            }
            if (
                note.p &&
                scale.findIndex(tone =>
                    tone.tone === p! &&
                    tone.a === ((note.p as IPitch).a || 0)
                )
                === -1
            ) {
                scale.push({
                    tone: p!,
                    piano:
                        60 +
                        pianoParser[(note.p as IPitch).s] +
                        ((note.p as IPitch).a || 0) +
                        getKeyOffset(doPos.value + 6) +
                        doPos.alt +
                        ((note.p as IPitch).o || 0) * 12 + octave * 12,
                    a: (note.p as IPitch).a || 0,
                    s: (note.p as IPitch).s,
                    o: ((note.p as IPitch).o || 0) + octave,
                    pitch: note.p as IPitch,
                });
            }
        }
    }
    scale.sort((a, b) => a.piano - b.piano);
    return { bars, scale, width, solutions, barlines: score.barlines, hasSharp, hasFlat };
};

// t = current time
// b = start value
// c = change in value
// d = duration
const easeInOutQuad = (t: number, b: number, c: number, d: number) => {
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t + b;
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
};

export function scrollTo(element: HTMLDivElement, to: number, duration: number, setTimer: (timer: ReturnType<typeof window.requestAnimationFrame>) => void) {
    let start = element.scrollLeft,
        change = to - start,
        currentTime = 0,
        timeOffset: number;
    const animateScroll = (timestamp: number) => {
        if (!timeOffset) timeOffset = timestamp;
        currentTime = timestamp - timeOffset;
        let val = easeInOutQuad(currentTime, start, change, duration);
        element.scrollLeft = val;
        if (currentTime < duration) {
            let timer = window.requestAnimFrame(animateScroll);
            setTimer(timer);
        }
    };
    let timer = window.requestAnimFrame(animateScroll);
    setTimer(timer);
}

export const isHintable = ({ bars, bar, barIndex, noteIndex, hint }: IHintableProps) => {
    let firstNonRest = true;
    for (let n in bar) {
        let hintable = false;
        const note = bar[n];
        if (note.p !== null && note.p !== undefined) {
            const firstBar = bars[0].length > 0 ? 0 : 1;
            if (hint && barIndex === firstBar && firstNonRest) {
                hintable = true;
            }
            firstNonRest = false;
        }
        if (+n === noteIndex) return hintable;
    }
    return false;
};

export function setStateAsync(this: any, state: Partial<IState>) {
    return new Promise(resolve => {
        this.setState(state, resolve);
    });
}
